import { useState, useCallback, useRef, useEffect } from 'react'

interface UseResizablePanelOptions {
  defaultWidth: number
  minWidth: number
  maxWidth: number
  storageKey: string
  /**
   * When true, the panel is right-anchored (handle on its LEFT edge): dragging
   * left widens it. Default false = left-anchored (handle on the right edge).
   */
  invert?: boolean
}

interface HandleProps {
  onMouseDown: (e: React.MouseEvent) => void
  onDoubleClick: () => void
  style: React.CSSProperties
  role: string
  'aria-label': string
}

export function useResizablePanel({
  defaultWidth,
  minWidth,
  maxWidth,
  storageKey,
  invert = false,
}: UseResizablePanelOptions) {
  const [width, setWidth] = useState(() => {
    const stored = localStorage.getItem(storageKey)
    if (stored) {
      const parsed = Number(stored)
      if (!Number.isNaN(parsed) && parsed >= minWidth && parsed <= maxWidth) {
        return parsed
      }
    }
    return defaultWidth
  })
  const [isResizing, setIsResizing] = useState(false)

  const isDraggingRef = useRef(false)
  const rafRef = useRef<number>(0)
  const startXRef = useRef(0)
  const startWidthRef = useRef(0)

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDraggingRef.current) return

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }

      rafRef.current = requestAnimationFrame(() => {
        const rawDelta = e.clientX - startXRef.current
        const delta = invert ? -rawDelta : rawDelta
        const newWidth = Math.min(maxWidth, Math.max(minWidth, startWidthRef.current + delta))
        setWidth(newWidth)
      })
    },
    [minWidth, maxWidth, invert]
  )

  const handleMouseUp = useCallback(() => {
    if (!isDraggingRef.current) return

    isDraggingRef.current = false
    setIsResizing(false)
    document.body.style.cursor = ''
    document.body.style.userSelect = ''

    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
    }

    // Persist to localStorage
    setWidth((currentWidth) => {
      localStorage.setItem(storageKey, String(currentWidth))
      return currentWidth
    })

    document.removeEventListener('mousemove', handleMouseMove)
    document.removeEventListener('mouseup', handleMouseUp)
  }, [storageKey, handleMouseMove])

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      isDraggingRef.current = true
      setIsResizing(true)
      startXRef.current = e.clientX
      startWidthRef.current = width

      document.body.style.cursor = 'col-resize'
      document.body.style.userSelect = 'none'

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    },
    [width, handleMouseMove, handleMouseUp]
  )

  const handleDoubleClick = useCallback(() => {
    setWidth(defaultWidth)
    localStorage.setItem(storageKey, String(defaultWidth))
  }, [defaultWidth, storageKey])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [handleMouseMove, handleMouseUp])

  const handleProps: HandleProps = {
    onMouseDown: handleMouseDown,
    onDoubleClick: handleDoubleClick,
    style: { cursor: 'col-resize' },
    role: 'separator',
    'aria-label': 'Resize sidebar',
  }

  return { width, isResizing, handleProps }
}
